import {
  format,
  eachDayOfInterval,
  isSameDay,
  differenceInCalendarDays,
  differenceInHours,
  eachWeekOfInterval,
  eachMonthOfInterval,
  eachYearOfInterval,
} from 'date-fns';
import i18n from '../i18n';
import { sumArrays } from './helper';
import {
  IAlarmTypes,
  IKpi,
  IAverageKpisReturn,
  IGetMttrVsFailsChartData,
  ILossesProfileReturn,
  IQlpTemporary,
  IWorkcenter,
} from '../types/kpis';
import { IOrder, IOrderDetailed, IOrderForm } from '../types/orders';

interface ISerie {
  name: string;
  data: number[];
}

interface IReturn {
  series: ISerie[];
  chartDates: number[];
  days: Date[];
}

interface IAgingReturn {
  agingData: ISerie[];
  agingLabels: number[];
}

interface ICountAlarmsReturn {
  alarmTypes: string[];
  countAlarmsData: ISerie[];
}

interface ICalculateExecutionRateReturn {
  executionRateData: ISerie[];
  weeks: number[];
}

interface ICountNotifications {
  needMaterial: number;
  needStructuralWork: number;
}

interface IOrderProfile {
  servicesTypes: string[];
  chartData: ISerie[];
}

interface ICalculateAverageKpisReturn {
  kpis: IAverageKpisReturn[];
  labels: string[];
}

interface IHandleMissingData {
  status: string;
  formattedData: number[];
}

interface ICalculateOrdersStatisticsByInterval {
  ordersCreationIncrease: number;
  chartData: number[];
}

export function cumulativeSum(arr: number[]): number[] {
  const cumsum = [];
  let total = 0;
  for (let i = 0; i < arr.length; i += 1) {
    total += arr[i];
    if (i === 0) {
      cumsum.push(arr[i]);
    } else {
      cumsum.push(total);
    }
  }

  return cumsum;
}

export function handleMissingData(data: number[]): IHandleMissingData {
  let status = '';
  const sum = data.reduce((a, b) => a + b);
  if (sum === -1) {
    status = 'No Data';
  }

  const formattedData = data.map(val => {
    if (val === -1) {
      return 0;
    }
    return val;
  });

  return { status, formattedData };
}

export function countByDateByPriority(
  orders: IOrder[],
  startDate: Date,
  endDate: Date,
): IReturn {
  const selectedOrders = orders.filter(
    order =>
      new Date(order.timestamp) >= startDate &&
      new Date(order.timestamp) <= endDate,
  );

  const eachDay = eachDayOfInterval({
    start: startDate,
    end: endDate,
  });

  const lowPriorityOrders = selectedOrders.filter(
    order => order.priority === 'BAIXA',
  );
  const averagePriorityOrders = selectedOrders.filter(
    order => order.priority === 'MEDIA',
  );
  const highPriorityOrders = selectedOrders.filter(
    order => order.priority === 'ALTA',
  );
  const urgentPriorityOrders = selectedOrders.filter(
    order => order.priority === 'URGENTE',
  );

  const lowPriorityCount: number[] = [];
  const averagePriorityCount: number[] = [];
  const highPriorityCount: number[] = [];
  const urgentPriorityCount: number[] = [];

  for (let i = 0; i < eachDay.length; i += 1) {
    lowPriorityCount.push(
      lowPriorityOrders.filter(order =>
        isSameDay(new Date(order.timestamp), eachDay[i]),
      ).length,
    );
    averagePriorityCount.push(
      averagePriorityOrders.filter(order =>
        isSameDay(new Date(order.timestamp), eachDay[i]),
      ).length,
    );
    highPriorityCount.push(
      highPriorityOrders.filter(order =>
        isSameDay(new Date(order.timestamp), eachDay[i]),
      ).length,
    );
    urgentPriorityCount.push(
      urgentPriorityOrders.filter(order =>
        isSameDay(new Date(order.timestamp), eachDay[i]),
      ).length,
    );
  }

  const chartDates = eachDay.map(day => day.getTime());

  const series = [
    {
      name: 'Baixa',
      data: lowPriorityCount,
    },
    {
      name: 'Média',
      data: averagePriorityCount,
    },
    {
      name: 'Alta',
      data: highPriorityCount,
    },
    {
      name: 'Urgente',
      data: urgentPriorityCount,
    },
  ];

  return { series, chartDates, days: eachDay };
}

export function calculateOrdersAging(orders: IOrder[]): IAgingReturn {
  const today = new Date();

  const lowPriorityOrders = orders.filter(order => order.priority === 'BAIXA');
  const averagePriorityOrders = orders.filter(
    order => order.priority === 'MEDIA',
  );
  const highPriorityOrders = orders.filter(order => order.priority === 'ALTA');
  const urgentPriorityOrders = orders.filter(
    order => order.priority === 'URGENTE',
  );

  const allOrdersAge: number[] = [];
  const lowPriorityAge: number[] = [];
  const averagePriorityAge: number[] = [];
  const highPriorityAge: number[] = [];
  const urgentPriorityAge: number[] = [];
  const lowPriorityAgeCount: number[] = [];
  const averagePriorityAgeCount: number[] = [];
  const highPriorityAgeCount: number[] = [];
  const urgentPriorityAgeCount: number[] = [];

  orders.forEach(order =>
    allOrdersAge.push(
      differenceInCalendarDays(today, new Date(order.timestamp)),
    ),
  );

  lowPriorityOrders.forEach(order =>
    lowPriorityAge.push(
      differenceInCalendarDays(today, new Date(order.timestamp)),
    ),
  );

  averagePriorityOrders.forEach(order =>
    averagePriorityAge.push(
      differenceInCalendarDays(today, new Date(order.timestamp)),
    ),
  );

  highPriorityOrders.forEach(order =>
    highPriorityAge.push(
      differenceInCalendarDays(today, new Date(order.timestamp)),
    ),
  );

  urgentPriorityOrders.forEach(order =>
    urgentPriorityAge.push(
      differenceInCalendarDays(today, new Date(order.timestamp)),
    ),
  );

  const agingLabels = [...Array.from(new Set(allOrdersAge))].sort(
    (a, b) => a - b,
  );

  for (let i = 0; i < agingLabels.length; i += 1) {
    lowPriorityAgeCount.push(
      lowPriorityAge.filter(age => age === agingLabels[i]).length,
    );
    averagePriorityAgeCount.push(
      averagePriorityAge.filter(age => age === agingLabels[i]).length,
    );
    highPriorityAgeCount.push(
      highPriorityAge.filter(age => age === agingLabels[i]).length,
    );
    urgentPriorityAgeCount.push(
      urgentPriorityAge.filter(age => age === agingLabels[i]).length,
    );
  }

  const agingData = [
    {
      name: 'Baixa',
      data: lowPriorityAgeCount,
    },
    {
      name: 'Média',
      data: averagePriorityAgeCount,
    },
    {
      name: 'Alta',
      data: highPriorityAgeCount,
    },
    {
      name: 'Urgente',
      data: urgentPriorityAgeCount,
    },
  ];

  return { agingData, agingLabels };
}

export function countOrdersAlarms(
  orders: IOrderDetailed[],
): ICountAlarmsReturn {
  const ordersWithAlarms = orders.filter(order => order.alarm_count.length > 0);
  const lowPriorityOrders = ordersWithAlarms.filter(
    order => order.priority === 'BAIXA',
  );
  const averagePriorityOrders = ordersWithAlarms.filter(
    order => order.priority === 'MEDIA',
  );
  const highPriorityOrders = ordersWithAlarms.filter(
    order => order.priority === 'ALTA',
  );
  const urgentPriorityOrders = ordersWithAlarms.filter(
    order => order.priority === 'URGENTE',
  );
  const allAlarms = [];

  for (let i = 0; i < ordersWithAlarms.length; i += 1) {
    for (let j = 0; j < ordersWithAlarms[i].alarm_count.length; j += 1) {
      allAlarms.push(ordersWithAlarms[i].alarm_count[j].type);
    }
  }

  const alarmTypes = [...Array.from(new Set(allAlarms))];

  const lowAlarmsCount = [];
  const aveAlarmsCount = [];
  const highAlarmsCount = [];
  const urgentAlarmsCount = [];

  for (let i = 0; i < alarmTypes.length; i += 1) {
    let lowCount = 0;
    let aveCount = 0;
    let highCount = 0;
    let urgentCount = 0;
    const lowOrdersAlarms = lowPriorityOrders
      .map(order =>
        order.alarm_count.filter(alarm => alarm.type === alarmTypes[i]),
      )
      .filter(alarm => alarm.length > 0);
    const aveOrdersAlarms = averagePriorityOrders
      .map(order =>
        order.alarm_count.filter(alarm => alarm.type === alarmTypes[i]),
      )
      .filter(alarm => alarm.length > 0);
    const highOrdersAlarms = highPriorityOrders
      .map(order =>
        order.alarm_count.filter(alarm => alarm.type === alarmTypes[i]),
      )
      .filter(alarm => alarm.length > 0);
    const urgentOrdersAlarms = urgentPriorityOrders
      .map(order =>
        order.alarm_count.filter(alarm => alarm.type === alarmTypes[i]),
      )
      .filter(alarm => alarm.length > 0);

    for (let j = 0; j < lowOrdersAlarms.length; j += 1) {
      lowCount += Number(lowOrdersAlarms[j][0].count);
    }
    for (let j = 0; j < aveOrdersAlarms.length; j += 1) {
      aveCount += Number(aveOrdersAlarms[j][0].count);
    }
    for (let j = 0; j < highOrdersAlarms.length; j += 1) {
      highCount += Number(highOrdersAlarms[j][0].count);
    }
    for (let j = 0; j < urgentOrdersAlarms.length; j += 1) {
      urgentCount += Number(urgentOrdersAlarms[j][0].count);
    }

    lowAlarmsCount.push(lowCount);
    aveAlarmsCount.push(aveCount);
    highAlarmsCount.push(highCount);
    urgentAlarmsCount.push(urgentCount);
  }

  const countAlarmsData = [
    {
      name: 'Baixa',
      data: lowAlarmsCount,
    },
    {
      name: 'Média',
      data: aveAlarmsCount,
    },
    {
      name: 'Alta',
      data: highAlarmsCount,
    },
    {
      name: 'Urgente',
      data: urgentAlarmsCount,
    },
  ];

  return { alarmTypes, countAlarmsData };
}

export function calculateExecutionRate(
  openOrders: IOrder[],
  finishedOrders: IOrder[],
  startDate: Date,
  endDate: Date,
): ICalculateExecutionRateReturn {
  const totalOpenOrders = [];
  const totalIncompleteOrders = [];
  const totalFinishedOrders = [];

  const dates = eachWeekOfInterval({
    start: startDate,
    end: endDate,
  });

  const weeks = dates.map(date => date.getTime());

  for (let i = 0; i < weeks.length; i += 1) {
    totalOpenOrders.push(
      openOrders.filter(
        order =>
          new Date(order.timestamp) >= dates[i] &&
          new Date(order.timestamp) < dates[i + 1] &&
          order.status === 'ABERTA',
      ).length,
    );
    totalIncompleteOrders.push(
      openOrders.filter(
        order =>
          new Date(order.timestamp) >= dates[i] &&
          new Date(order.timestamp) < dates[i + 1] &&
          order.status === 'INCOMPLETA',
      ).length,
    );
    totalFinishedOrders.push(
      finishedOrders.filter(
        order =>
          new Date(order.timestamp) >= dates[i] &&
          new Date(order.timestamp) < dates[i + 1],
      ).length,
    );
  }

  const executionRateData = [
    {
      name: i18n.t('screens:assetsHome.incompleteOrders'),
      data: totalIncompleteOrders,
    },
    {
      name: i18n.t('screens:assetsHome.finishedOrders'),
      data: totalFinishedOrders,
    },
    {
      name: i18n.t('screens:assetsHome.missingOrders'),
      data: totalOpenOrders,
    },
  ];

  return { executionRateData, weeks };
}

export function countNotifications(forms: IOrderForm[]): ICountNotifications {
  const needMaterial = forms.filter(
    form => form.notification_need_lining_material === 1,
  ).length;
  const needStructuralWork = forms.filter(
    form => form.notification_need_structural_work === 1,
  ).length;

  return { needMaterial, needStructuralWork };
}

export function calculateOrdersProfile(alarms: IAlarmTypes[]): IOrderProfile {
  const servicesTypes = [
    'Buraco',
    'Poeira',
    'Blocos',
    'Caimento da pista',
    'Trilha das rodas',
    'Borrachudo',
    'Talude',
    'Drenagem',
    'Ondulação',
  ];

  const chartData: ISerie[] = [];

  for (let i = 0; i < alarms.length; i += 1) {
    const splitAlarmName = alarms[i].alarm.split('_');
    let formattedName = '';

    if (splitAlarmName.length === 2) {
      const firstToUpperCase =
        splitAlarmName[0][0].toUpperCase() + splitAlarmName[0].substring(1);
      const secondToUpperCase =
        splitAlarmName[1][0].toUpperCase() + splitAlarmName[1].substring(1);
      formattedName = `${firstToUpperCase} ${secondToUpperCase}`;
    }

    if (splitAlarmName.length === 1) {
      const firstToUpperCase =
        splitAlarmName[0][0].toUpperCase() + splitAlarmName[0].substring(1);
      formattedName = `${firstToUpperCase}`;
    }

    const findNonZeroValue = alarms[i].form.find(val => val > 0);

    if (
      findNonZeroValue ||
      alarms[i].alarm === 'rack' ||
      alarms[i].alarm === 'bias' ||
      alarms[i].alarm === 'pitch' ||
      alarms[i].alarm === 'payload'
    ) {
      const alarmData = {
        name: formattedName,
        data: alarms[i].form,
      };
      chartData.push(alarmData);
    }
  }

  return { chartData, servicesTypes };
}

export function calculateAveragekpiByUnit(
  unitsIds: string[],
  kpis: IKpi[],
  startDate: Date,
  endDate: Date,
  timescale: string,
): ICalculateAverageKpisReturn {
  const result: IAverageKpisReturn[] = [];
  const labels: string[] = [];

  if (timescale === 'month') {
    const months = eachMonthOfInterval({ start: startDate, end: endDate });
    months.forEach(month => labels.push(format(month, 'MMM')));
  }

  if (timescale === 'week') {
    const weeks = eachWeekOfInterval({ start: startDate, end: endDate });
    for (let k = 0; k < weeks.length - 1; k += 1) {
      labels.push(
        `${format(weeks[k], 'dd')}-${format(weeks[k + 1], 'dd')}/${format(
          weeks[k + 1],
          'MM',
        )}`,
      );
    }
  }

  if (timescale === 'year') {
    const years = eachYearOfInterval({ start: startDate, end: endDate });
    years.forEach(year => labels.push(format(year, 'yyyy')));
  }

  for (let i = 0; i < unitsIds.length; i += 1) {
    const unitKpis = kpis.filter(kpi => kpi.unit_id === unitsIds[i]);

    if (unitKpis.length === 0) {
      const data = {
        unit_id: unitsIds[i],
        averageMttr: -1,
        averageMtbf: -1,
        averageDf: -1,
      };
      result.push(data);
    } else {
      const mttr: number[] = [];
      const mtbf: number[] = [];
      const mttrWithoutMissingData: number[] = [];
      const mtbfWithoutMissingData: number[] = [];
      for (let j = 0; j < unitKpis.length; j += 1) {
        unitKpis[j].mtbf.forEach(val => {
          mtbf.push(val);
          if (val === -1) {
            mtbfWithoutMissingData.push(0);
          } else {
            mtbfWithoutMissingData.push(val);
          }
        });
        unitKpis[j].mttr.forEach(val => {
          mttr.push(val);
          if (val === -1) {
            mttrWithoutMissingData.push(0);
          } else {
            mttrWithoutMissingData.push(val);
          }
        });
      }

      const sumMttr = mttrWithoutMissingData.reduce((sum, acc) => sum + acc);
      const sumMtbf = mtbfWithoutMissingData.reduce((sum, acc) => sum + acc);

      // DF = [MTBF/MTBF+MTTR] * 100
      const averageDf =
        sumMtbf === 0 ? 0 : (sumMtbf / (sumMtbf + sumMttr)) * 100;

      const data = {
        unit_id: unitsIds[i],
        averageMttr: sumMttr / mttr.length,
        averageMtbf: sumMtbf / mtbf.length,
        averageDf,
      };

      result.push(data);
    }
  }

  return { kpis: result, labels };
}

export function getMttrVsFailsChartData(
  mttr: number[],
  fails: number[],
): IGetMttrVsFailsChartData {
  const minMttrMaxValue = 80;
  const minFailMaxValue = 100;
  const minMttrMiddleValue = 40;
  const minFailMiddleValue = 50;
  const maxMttrValue = mttr.reduce((a, b) => Math.max(a, b));
  const maxFailValue = fails.reduce((a, b) => Math.max(a, b));
  const middleMttrValue = Math.round(maxMttrValue / 2);
  const middleFailValue = Math.round(maxFailValue / 2);

  let maxXValue;
  let middleXValue;
  let maxYValue;
  let middleYValue;

  if (maxMttrValue < minMttrMaxValue) {
    maxYValue = minMttrMaxValue;
    middleYValue = minMttrMiddleValue;
  } else {
    maxYValue = maxMttrValue;
    middleYValue = middleMttrValue;
  }

  if (maxFailValue < minFailMaxValue) {
    maxXValue = minFailMaxValue;
    middleXValue = minFailMiddleValue;
  } else {
    maxXValue = maxFailValue;
    middleXValue = middleFailValue;
  }

  const scatterData = mttr.map((val, index) => [fails[index], val]);
  const sharpFails = [
    [0, 0],
    [0, maxYValue],
    [middleXValue, maxYValue],
    [middleXValue, 0],
    [maxXValue, 0],
  ];

  const noFails = [
    [0, 0],
    [0, middleYValue],
    [middleXValue, middleYValue],
    [middleXValue, 0],
    [maxXValue, 0],
  ];

  const chronicAndSharpFails = [
    [0, 0],
    [middleXValue, 0],
    [middleXValue, maxYValue],
    [maxXValue, maxYValue],
    [maxXValue, 0],
  ];

  const chronicFails = [
    [0, 0],
    [middleXValue, 0],
    [middleXValue, middleYValue],
    [maxXValue, middleYValue],
    [maxXValue, 0],
  ];

  return {
    scatterData,
    sharpFails,
    noFails,
    chronicAndSharpFails,
    chronicFails,
  };
}

export function calculateLossesProfile(
  forms: IOrderForm[],
): ILossesProfileReturn {
  const stHole = forms.filter(form => form.st_hole === 1);
  const stDust = forms.filter(form => form.st_dust === 1);
  const stBoulders = forms.filter(form => form.st_boulders === 1);
  const stRoadSlope = forms.filter(form => form.st_road_slope === 1);
  const stTruckTrails = forms.filter(form => form.st_truck_trails === 1);
  const stStrut = forms.filter(form => form.st_strut === 1);
  const stSlope = forms.filter(form => form.st_slope === 1);
  const stDrainage = forms.filter(form => form.st_drainage === 1);
  const stWavyTrack = forms.filter(form => form.st_wavy_track === 1);

  const stHoleTime = stHole.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stDustTime = stDust.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stBouldersTime = stBoulders.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stRoadSlopeTime = stRoadSlope.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stTruckTrailsTime = stTruckTrails.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stStrutTime = stStrut.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stSlopeTime = stSlope.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stDrainageTime = stDrainage.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );
  const stWavyTrackTime = stWavyTrack.map(form =>
    differenceInHours(
      new Date(form.timestamp_end),
      new Date(form.timestamp_start),
    ),
  );

  const totalStHole =
    stHoleTime.length > 0 ? stHoleTime.reduce((a, b) => a + b) : 0;
  const totalStDust =
    stDustTime.length > 0 ? stDustTime.reduce((a, b) => a + b) : 0;
  const totalStBoulders =
    stBouldersTime.length > 0 ? stBouldersTime.reduce((a, b) => a + b) : 0;
  const totalStRoadSlope =
    stRoadSlopeTime.length > 0 ? stRoadSlopeTime.reduce((a, b) => a + b) : 0;
  const totalStTruckTrails =
    stTruckTrailsTime.length > 0
      ? stTruckTrailsTime.reduce((a, b) => a + b)
      : 0;
  const totalStStrut =
    stStrutTime.length > 0 ? stStrutTime.reduce((a, b) => a + b) : 0;
  const totalStSlope =
    stSlopeTime.length > 0 ? stSlopeTime.reduce((a, b) => a + b) : 0;
  const totalStDrainage =
    stDrainageTime.length > 0 ? stDrainageTime.reduce((a, b) => a + b) : 0;
  const totalStWavyTrack =
    stWavyTrackTime.length > 0 ? stWavyTrackTime.reduce((a, b) => a + b) : 0;

  const lossesProfileData = [
    totalStSlope,
    totalStDust,
    totalStHole,
    totalStStrut,
    totalStBoulders,
    totalStTruckTrails,
    totalStRoadSlope,
    totalStWavyTrack,
    totalStDrainage,
  ];

  const total = lossesProfileData.reduce((a, b) => a + b);

  const accumulatedTotal = cumulativeSum(lossesProfileData);
  const accumulatedPercentage = accumulatedTotal.map(
    val => (val / total) * 100,
  );

  return { lossesProfileData, accumulatedTotal, accumulatedPercentage };
}

export function calculateAverageRoadCondition(kpiData: IKpi): number[] {
  const roadConditions = kpiData.road_condition;
  const conditionOne = handleMissingData(roadConditions['1']).formattedData;
  const conditionTwo = handleMissingData(roadConditions['2']).formattedData;
  const conditionThree = handleMissingData(roadConditions['3']).formattedData;

  const averageRoadCondition = [];

  for (let i = 0; i < conditionOne.length; i += 1) {
    const average = (conditionOne[i] + conditionTwo[i] + conditionThree[i]) / 3;
    averageRoadCondition.push(average);
  }

  return averageRoadCondition;
}

export function calculateOrdersStatisticsByInterval(
  orders: IOrderDetailed[],
  startDate: Date,
  endDate: Date,
): ICalculateOrdersStatisticsByInterval {
  // const today = new Date();
  // const startDay = subDays(today, 7);
  const olderOrders = orders.filter(
    order => new Date(order.timestamp) < startDate,
  );
  const allDays = eachDayOfInterval({
    start: startDate,
    end: endDate,
  });
  const ordersCreationIncrease =
    (orders.length - olderOrders.length) / olderOrders.length;

  const countOrders = [];

  for (let i = 0; i < allDays.length; i += 1) {
    const sameDayOrders = orders.filter(order =>
      isSameDay(allDays[i], new Date(order.timestamp)),
    );
    const olderThanCurrentDay = orders.filter(
      order => new Date(order.timestamp) < allDays[i],
    );

    countOrders.push(sameDayOrders.length + olderThanCurrentDay.length);
  }

  return { ordersCreationIncrease, chartData: countOrders };
}

export function calculateDfByInterval(
  kpiData: IKpi[],
  unitId: string,
): number[] {
  const filteredKpiData = kpiData.filter(data => data.unit_id === unitId);

  if (filteredKpiData.length === 0) {
    return [];
  }

  const justMttr = kpiData.map(data => data.mttr);
  const justMtbf = kpiData.map(data => data.mtbf);

  const sumMttr = sumArrays(...justMttr);
  const sumMtbf = sumArrays(...justMtbf);

  const averageMttrData = sumMttr.map(mttr => mttr / justMttr.length);
  const averageMtbfData = sumMtbf.map(mttr => mttr / justMtbf.length);

  // DF = [MTBF/MTBF+MTTR] * 100
  const dfData = averageMtbfData.map((mtbf, idx) => {
    if (mtbf === 0) {
      return 0;
    }
    return (mtbf / (mtbf + averageMttrData[idx])) * 100;
  });

  return dfData;
}

function sumByParameter(
  data: IQlpTemporary[],
  parameter:
    | 'annual'
    | 'mechanics'
    | 'electricians'
    | 'structural_inspectors'
    | 'mechanic_inspectors'
    | 'technicians'
    | 'lubricators',
): number[] {
  const filtered = data.map(dt => dt[parameter]);
  const sum = sumArrays(...filtered);
  const average = sum.map(val => val / filtered.length);
  return average;
}

export function calculateWorkcentersAverage(data: IWorkcenter[][]): number[] {
  const allValues: number[][] = [];

  data.forEach(workcenter => {
    workcenter.forEach(wk => allValues.push(wk.values));
  });

  const sum = sumArrays(...allValues);
  const average = sum.map(val => val / allValues.length);

  return average;
}

export function calculateQlpAverage(qlpData: IQlpTemporary[]): IQlpTemporary {
  const annual = sumByParameter(qlpData, 'annual');
  const mechanics = sumByParameter(qlpData, 'mechanics');
  const electricians = sumByParameter(qlpData, 'electricians');
  const structural_inspectors = sumByParameter(
    qlpData,
    'structural_inspectors',
  );
  const mechanic_inspectors = sumByParameter(qlpData, 'mechanic_inspectors');
  const technicians = sumByParameter(qlpData, 'technicians');
  const lubricators = sumByParameter(qlpData, 'lubricators');

  const res: IQlpTemporary = {
    year: qlpData[0].year,
    timescale: qlpData[0].timescale,
    annual,
    mechanics,
    electricians,
    structural_inspectors,
    mechanic_inspectors,
    technicians,
    lubricators,
  };

  return res;
}
